import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { PortalAuthProvider } from "@/context/PortalAuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LangProvider } from "@/i18n";
import ProtectedRoute from "@/components/crm/ProtectedRoute";
import PortalProtected from "@/components/portal/PortalProtected";

import Navbar from "@/components/landing/Navbar";
import HeroSection from "@/components/landing/HeroSection";
import BenefitsSection from "@/components/landing/BenefitsSection";
import PlansSection from "@/components/landing/PlansSection";
import HowItWorks from "@/components/landing/HowItWorks";
import TrustSection from "@/components/landing/TrustSection";
import ContactSection from "@/components/landing/ContactSection";
import FinalCTA from "@/components/landing/FinalCTA";
import Footer from "@/components/landing/Footer";
import SupportPage from "@/pages/SupportPage";
import { useLandingTheme, LandingThemeProvider } from "@/context/LandingThemeContext";
import LoginPage from "@/pages/LoginPage";
import ConsultarDados from "@/pages/ConsultarDados";
import NewSkyWifi from "@/pages/NewSkyWifi";

import CRMLayout from "@/components/crm/CRMLayout";
import Dashboard from "@/components/crm/Dashboard";
import CustomerList from "@/components/crm/CustomerList";
import CustomerForm from "@/components/crm/CustomerForm";
import CustomerDetail from "@/components/crm/CustomerDetail";
import ProductList from "@/components/crm/ProductList";
import ProductForm from "@/components/crm/ProductForm";
import SalesList from "@/components/crm/SalesList";
import SaleForm from "@/components/crm/SaleForm";
import SalesWizard from "@/components/crm/SalesWizard";
import SubscriptionList from "@/components/crm/SubscriptionList";
import PaymentList from "@/components/crm/PaymentList";
import ChatList from "@/components/crm/ChatList";
import ChatConversation from "@/components/crm/ChatConversation";
import OperatorList from "@/components/crm/OperatorList";
import OperatorsModule from "@/components/crm/OperatorsModule";
import AuditLog from "@/components/crm/AuditLog";
import SimLookup from "@/components/crm/SimLookup";
import StripeSettings from "@/components/crm/StripeSettings";

import PortalLayout from "@/components/portal/PortalLayout";
import PortalLogin from "@/components/portal/PortalLogin";
import PortalSetup from "@/components/portal/PortalSetup";
import PortalDashboard from "@/components/portal/PortalDashboard";
import PortalServices from "@/components/portal/PortalServices";
import PortalPayments from "@/components/portal/PortalPayments";
import PortalSupport from "@/components/portal/PortalSupport";
import PortalAccount from "@/components/portal/PortalAccount";

function LandingPageInner() {
  const { theme } = useLandingTheme();
  const isLight = theme === "light";
  return (
    <div className={`min-h-screen noise-overlay relative transition-colors duration-500 ${isLight ? "landing-light" : ""}`}
      style={{ background: isLight ? "#ffffff" : "#020617", color: isLight ? "#0f172a" : "#e2e8f0" }}>
      <Navbar />
      <HeroSection />
      <BenefitsSection />
      <PlansSection />
      <HowItWorks />
      <TrustSection />
      <ContactSection />
      <FinalCTA />
      <Footer />
    </div>
  );
}

const LandingPage = () => (
  <LandingThemeProvider>
    <LandingPageInner />
  </LandingThemeProvider>
);

function WifiPageInner() {
  const { theme } = useLandingTheme();
  const isLight = theme === "light";
  return (
    <div className={`min-h-screen noise-overlay relative transition-colors duration-500 ${isLight ? "landing-light" : ""}`}
      style={{ background: isLight ? "#ffffff" : "#020617", color: isLight ? "#0f172a" : "#e2e8f0" }}>
      <Navbar />
      <NewSkyWifi />
      <Footer />
    </div>
  );
}

const WifiPage = () => (
  <LandingThemeProvider>
    <WifiPageInner />
  </LandingThemeProvider>
);

function App() {
  return (
    <BrowserRouter>
      <LangProvider>
      <ThemeProvider>
        <Routes>
          {/* Public */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/wifi" element={<WifiPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/consultar-dados" element={<ConsultarDados />} />

          {/* CRM */}
          <Route path="/crm/login" element={<AuthProvider><LoginPage /></AuthProvider>} />
          <Route path="/crm" element={<AuthProvider><ProtectedRoute><CRMLayout /></ProtectedRoute></AuthProvider>}>
            <Route index element={<Dashboard />} />
            <Route path="customers" element={<CustomerList />} />
            <Route path="customers/new" element={<CustomerForm />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="customers/:id/edit" element={<CustomerForm />} />
            <Route path="products" element={<ProductList />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:id" element={<ProductForm />} />
            <Route path="sales" element={<SalesList />} />
            <Route path="sales/new" element={<SaleForm />} />
            <Route path="sales/wizard" element={<SalesWizard />} />
            <Route path="subscriptions" element={<SubscriptionList />} />
            <Route path="payments" element={<PaymentList />} />
            <Route path="chat" element={<ChatList />} />
            <Route path="chat/:customerId" element={<ChatConversation />} />
            <Route path="operators" element={<OperatorsModule />} />
            <Route path="sim-lookup" element={<SimLookup />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="settings/stripe" element={<StripeSettings />} />
          </Route>

          {/* Portal */}
          <Route path="/portal/login" element={<PortalAuthProvider><PortalLogin /></PortalAuthProvider>} />
          <Route path="/portal/setup/:token" element={<PortalAuthProvider><PortalSetup /></PortalAuthProvider>} />
          <Route path="/portal" element={<PortalAuthProvider><PortalProtected><PortalLayout /></PortalProtected></PortalAuthProvider>}>
            <Route index element={<PortalDashboard />} />
            <Route path="services" element={<PortalServices />} />
            <Route path="payments" element={<PortalPayments />} />
            <Route path="support" element={<PortalSupport />} />
            <Route path="account" element={<PortalAccount />} />
          </Route>
        </Routes>
      </ThemeProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

export default App;
